// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { AppIcon, Button, Checkbox, Radio } from '@loa/ui-components';
import type {
  AnswerMap,
  ClientOption,
  WizardField,
  WizardGate,
  WizardGroup,
  WizardIntegration,
  WizardSection,
} from './types';
import WizardFieldInput, {
  DerivedLockedFieldsContext,
  FieldErrorContext,
  FieldErrorMessage,
  SuppressPlaceholdersContext,
  WIZARD_RADIO_OVERRIDE,
  WIZARD_SUBLABEL_CLASS,
  type RenderDocumentSlot,
  type RenderOptionsSelectSlot,
} from './WizardFieldInput';
import {
  applyFieldChange,
  areSectionFieldsComplete,
  cleanSectionAnswers,
  gateState,
  isGroupComplete,
  isGroupRevealed,
  isSectionComplete,
  optionLabel,
  optionValue,
  pendingDefaults,
  sectionAnswersFingerprint,
  showIfSatisfied,
  soloOwnerSeed,
  toggleGateOption,
  visibleFields,
} from './wizardLogic';
import { currencySymbol } from './currency';
import { formatAmount } from './numberFormat';
import { formatAge } from './age';
import { ProviderLogo } from './ProviderLogo';

const REVEAL_ALL = new Set(['*']);

/**
 * Provider name → logo URL, supplied by the host (the Client wizard builds it from
 * the brokerage's enabled-providers catalogue). Read by a collapsed card to show
 * the provider's logo; an unknown name falls back to an initials monogram.
 */
const ProviderLogosContext = React.createContext<Record<string, string>>({});
const EMPTY_PROVIDER_LOGOS: Record<string, string> = {};

/** Sentinel `pendingSaveKeyRef` value: a PER_CLIENT section save collapses every
 *  client block (rather than one keyed card) on success. */
const PER_CLIENT_SAVE_KEY = '__perClient__';

/** Sentinel `pendingSaveKeyRef` value: a section-level save (gate-only / field-grid
 *  steps with no repeating cards, e.g. Dependants = "No", Expenses) collapses the
 *  WHOLE section to its title header on success. */
const SECTION_SAVE_KEY = '__section__';

/**
 * Whether `advisorOnly` fields render. Provided by the section editor from its
 * `showAdvisorOnlyFields` prop and read by FieldGrid. Default true → shown
 * (Advisor accordion / tests); the Client wizard provides false to hide them.
 */
const AdvisorOnlyVisibilityContext = React.createContext(true);

const WIZARD_SQUIRCLE_CHECKBOX_OVERRIDE =
  '[&_.fin-checkbox__control]:w-[22px]! [&_.fin-checkbox__control]:h-[22px]! ' +
  '[&_.fin-checkbox__control]:border-[1.5px]! [&_.fin-checkbox__control]:border-solid! ' +
  '[&_.fin-checkbox__control]:border-purple-primary! [&_.fin-checkbox__control]:rounded-[6px]! ' +
  '[&_.fin-checkbox__input:checked+.fin-checkbox__control]:bg-purple-primary! ' +
  '[&_.fin-checkbox__input:checked+.fin-checkbox__control]:border-purple-primary!';
const EMPLOYMENT_GROUP_ID = 'employments';
const EMPLOYMENT_GROUP_TITLE = 'Current Employment Details';
const EMPLOYMENT_EXTRAS_LABEL = 'Do you have any of the following related to this employer?';
// The employment-type gate field. job title / employer / base salary and the
// extras toggles are `showIf`-gated on it; the bonus/share/BIK *amount* fields
// instead reveal off their own toggle, so they're identified by a `showIf` that
// references something OTHER than this field.
const EMPLOYMENT_TYPE_FIELD_ID = 'employmentType';
const OTHER_INCOME_SECTION_ID = 'otherIncome';
const OTHER_INCOME_GATE_ID = 'incomeSources';
const EXPENSES_SECTION_ID = 'expenses';
const PROPERTY_SECTION_ID = 'property';
const PROPERTY_LIVING_SITUATION_TITLE = 'Your living situation';
const PROPERTY_LIVING_SITUATION_FIELD_ID = 'livingSituation';
const PROPERTY_INVESTMENTS_GROUP_ID = 'investmentProperties';
const PROTECTION_SECTION_ID = 'protection';
const PROTECTION_GATE_ID = 'protectionTypes';
// Protection's always-visible "New protection policies" block. It is a NON-repeating
// group (so no per-card save) that renders its OWN Save Details footer, gated on its own
// required fields + a protectionTypes selection — see renderGroup.
const PROTECTION_NEW_POLICY_GROUP_ID = 'newPolicyInterest';
// Sections that render a Save Details + Remove inside each white bordered card
// (per repeating item) instead of a single section-level Save — see renderGroup.
const DEPENDANTS_SECTION_ID = 'dependants';
const CURRENT_EMPLOYMENT_SECTION_ID = 'currentEmployment';
const OTHER_ADVISORS_GATE_ID = 'advisorTypes';
const PENSIONS_INVESTED_FIELD_IDS = [
  'investedCash',
  'investedFunds',
  'investedStocks',
  'investedProperty',
  'investedUnknown',
];
const PENSIONS_INVESTED_HEADING = 'How is the pension being invested? Select all that apply.';
// The pensions "This policy is in agency" control sits under a "For Advisor Only"
// heading at the foot of each pension card (visual grouping only).
const PENSIONS_IN_AGENCY_FIELD_ID = 'inAgency';
const FOR_ADVISOR_ONLY_LABEL = 'For Advisor Only';
const DESKTOP_TITLE_SECTION_IDS = [
  DEPENDANTS_SECTION_ID,
  CURRENT_EMPLOYMENT_SECTION_ID,
  OTHER_INCOME_SECTION_ID,
  EXPENSES_SECTION_ID,
  'property',
  'liabilities',
  'savings',
  'investments',
  'pensions',
  'protection',
  'otherAdvisors',
  'goals',
];
// Sections that use the per-card Save Details + outlined Remove over white
// bordered cards (instead of a single section-level Save).
const PER_GROUP_SAVE_SECTION_IDS = new Set([
  DEPENDANTS_SECTION_ID,
  CURRENT_EMPLOYMENT_SECTION_ID,
  OTHER_INCOME_SECTION_ID,
  PROPERTY_SECTION_ID,
  'liabilities',
  'savings',
  'investments',
  'pensions',
  PROTECTION_SECTION_ID,
]);

// ---- Collapsed-card summary --------------------------------------------------
// A saved card collapses to a bold title + a "part • part" subline. Dependants
// match the design exactly (name / relationship • age); other repeating groups
// fall back to a best-effort summary derived from their filled fields.

function isFilledValue(value: unknown): boolean {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (typeof value === 'object') {
    const m = value as AnswerMap;
    if ('amount' in m) return m.amount !== undefined && m.amount !== null;
    return Object.keys(m).length > 0;
  }
  return true; // numbers, booleans
}

function formatSummaryValue(
  field: WizardField,
  value: unknown,
  item: AnswerMap,
  clients: ClientOption[],
): string {
  switch (field.type) {
    case 'money': {
      const m = (value && typeof value === 'object' ? value : {}) as AnswerMap;
      if (typeof m.amount !== 'number') return '';
      const sym = field.currencyFromField ? currencySymbol(item[field.currencyFromField]) : '€';
      return `${sym}${formatAmount(m.amount)}`;
    }
    case 'enum': {
      const opt = (field.options ?? []).find((o) => optionValue(o) === String(value));
      return opt ? optionLabel(opt) : String(value ?? '');
    }
    case 'clientRef':
      if (value === 'JOINT') return 'Both';
      return clients.find((c) => c.id === value)?.name ?? String(value ?? '');
    default:
      return typeof value === 'string' ? value : String(value ?? '');
  }
}

/**
 * Title + subline parts for a collapsed repeating-group card. Dependants get the
 * exact design (full name, then `relationship • age`); any other group uses a
 * full name when present, else its first filled string/enum/clientRef as the
 * title, and up to two more filled string/enum/money/clientRef fields as parts.
 */
function summarizeItem(
  group: WizardGroup,
  item: AnswerMap,
  clients: ClientOption[],
): { title: string; parts: string[]; providerName?: string; owner?: string } {
  const fullName = [item.firstName, item.lastName]
    .filter((p) => typeof p === 'string' && p.trim() !== '')
    .join(' ')
    .trim();

  if (group.id === DEPENDANTS_SECTION_ID) {
    const parts: string[] = [];
    if (typeof item.relationship === 'string' && item.relationship) parts.push(item.relationship);
    const age = formatAge(item.dateOfBirth);
    if (age) parts.push(age);
    return { title: fullName || 'Dependant', parts };
  }

  // The provider (rendered as the left logo) and the owner (a right-aligned chip)
  // are surfaced separately from the title/subtitle so the closed card matches the
  // reference design — both are excluded from the title + parts to avoid repetition.
  const providerField = group.fields.find((f) => f.optionsSource?.type === 'PROVIDERS');
  const providerName =
    providerField &&
    typeof item[providerField.id] === 'string' &&
    (item[providerField.id] as string).trim() !== ''
      ? (item[providerField.id] as string)
      : undefined;
  const ownerField = group.fields.find((f) => f.type === 'clientRef' && isFilledValue(item[f.id]));
  const owner = ownerField
    ? formatSummaryValue(ownerField, item[ownerField.id], item, clients)
    : undefined;

  const filled = group.fields.filter((f) => isFilledValue(item[f.id]));
  const titleField = fullName
    ? undefined
    : ['string', 'enum', 'clientRef']
        .map((t) => filled.find((f) => f.type === t && f !== ownerField && f !== providerField))
        .find(Boolean);
  const title = fullName
    ? fullName
    : titleField
      ? formatSummaryValue(titleField, item[titleField.id], item, clients)
      : (group.title ?? 'Details');

  const parts = filled
    .filter(
      (f) =>
        f !== titleField &&
        f !== ownerField &&
        f !== providerField &&
        f.id !== 'firstName' &&
        f.id !== 'lastName' &&
        ['string', 'enum', 'money', 'clientRef'].includes(f.type),
    )
    .slice(0, 2)
    .map((f) => formatSummaryValue(f, item[f.id], item, clients))
    .filter((s) => s.trim() !== '');

  return { title: title || (group.title ?? 'Details'), parts, providerName, owner };
}
// -----------------------------------------------------------------------------

export interface SectionError {
  questionId: string;
  message: string;
}

/**
 * Push values from a `section.integration` into the section's live answers. Pass a
 * plain map to shallow-overwrite those keys, or an updater to decide against the
 * current answers — the Open Banking import uses the updater so it can seed empty
 * fields without clobbering cards the client already filled in.
 */
export type SuggestAnswers = (
  values: AnswerMap | ((prev: AnswerMap) => AnswerMap),
) => void;

export interface WizardSectionFormProps {
  section: WizardSection;
  clients: ClientOption[];
  initialAnswers: AnswerMap;
  saving: boolean;
  errors: SectionError[];
  /**
   * Persist the section. `meta.advance` is true only for the section-level
   * "Save Details" (the "I'm done with this step" button) — NOT for per-card
   * saves inside a repeating group — so the host can move to the next step on a
   * step-completing save without jumping away mid-way through adding cards.
   */
  onSave: (answers: AnswerMap, meta?: { advance?: boolean }) => void;
  /** Render a `section.integration` banner (client injects OPEN_BANKING/DOCUMENT). */
  renderIntegration?: (
    integration: WizardIntegration,
    onSuggest: SuggestAnswers,
  ) => React.ReactNode;
  /** Render `document` fields' upload UI (client injects its document API). */
  renderDocument?: RenderDocumentSlot;
  renderOptionsSelect?: RenderOptionsSelectSlot;
  /** Render a CHAT section's guided conversation. Omit → CHAT falls back to the form. */
  renderChat?: (args: {
    section: WizardSection;
    saving: boolean;
    onComplete: (answers: AnswerMap) => void;
    onUseFormInstead: () => void;
  }) => React.ReactNode;
  saveLabel?: string;
  hideChrome?: boolean;
  /**
   * Suppress every input placeholder in this section's fields. The Client Fact
   * Find wizard turns this on so the form shows no placeholder hint text;
   * advisor-ui omits it (placeholders preserved).
   */
  suppressPlaceholders?: boolean;
  /**
   * Whether fields tagged `advisorOnly` (e.g. the protection/pensions "This policy
   * is in agency" flag) render. Default true → shown (Advisor accordion). The
   * Client Fact Find wizard passes false to hide the "For Advisor Only" block.
   */
  showAdvisorOnlyFields?: boolean;
  /**
   * Open every container of this section COLLAPSED on (re)mount. The Client wizard
   * sets this when re-entering a section that was already saved (going back to a
   * COMPLETE / partially-saved step) so it shows the tidy saved state instead of
   * every form expanded. A never-saved section leaves this false → opens expanded.
   */
  startCollapsed?: boolean;
  /**
   * Field ids that are currently auto-populated from ANOTHER section (e.g. Expenses
   * "education costs" ← Dependants "fees"). They render locked + greyed (same as the
   * Personal Details prefilled identity fields). Defaults to none → all editable.
   */
  lockedFields?: ReadonlySet<string>;
  /**
   * Client wizard only: hide the `clientRef` "who does this belong to" owner
   * pickers for a SOLO relationship (a single client on the profile) and auto-seed
   * that client as the owner instead. A solo relationship has exactly one possible
   * owner, so the pickers are pointless and block "Save Details". Off by default →
   * the Advisor accordion (and any joint relationship) keeps showing the pickers.
   * See {@link soloOwnerSeed} and {@link visibleFields}'s `soloClientId` argument.
   */
  hideSoloOwnerFields?: boolean;
  /**
   * Provider name → logo URL (from the brokerage's enabled-providers catalogue).
   * A collapsed repeating card shows the provider's logo as its left avatar;
   * a name with no entry falls back to an initials monogram. Omit → all monograms.
   */
  providerLogos?: Record<string, string>;
  /**
   * Notifies the host when this section has unsaved edits (current answers
   * diverge from the saved baseline). Used by the Client wizard to warn before
   * leaving the page; advisor-ui omits it.
   */
  onDirtyChange?: (dirty: boolean) => void;
  /**
   * Seed the working buffer from a host-preserved value. The Client wizard
   * remounts a fresh editor on every step switch (keyed by section id); passing
   * the host's preserved per-section working answers here restores the user's
   * unsaved edits "as they left them" when they navigate back. Omit → the buffer
   * seeds from `initialAnswers` (advisor accordion / first visit). `initialAnswers`
   * stays the SAVED server slice, so the dirty baseline is still computed against
   * the server (edits restored via `workingAnswers` correctly read as dirty).
   */
  workingAnswers?: AnswerMap;
  /**
   * Mirror the live working answers to the host on every change, so they survive
   * this editor unmounting on a step switch. Advisor-ui omits it.
   */
  onAnswersChange?: (answers: AnswerMap) => void;
  /**
   * The settled "saved" dirty baseline, supplied by the host across remounts.
   * When `workingAnswers` restores unsaved edits, the editor's own baseline would
   * otherwise be captured FROM those edits (reading clean). The host establishes
   * the baseline once from the server state (via {@link onBaselineEstablished})
   * and feeds it back here so restored edits correctly read as dirty. `null`/
   * omitted → the editor self-establishes the baseline (first visit / advisor-ui).
   */
  externalBaseline?: string | null;
  /**
   * Reports the settled "saved" baseline fingerprint the first time the editor
   * establishes it (and after each save), so the host can persist it per-section
   * and feed it back as {@link externalBaseline} on the next remount.
   */
  onBaselineEstablished?: (fingerprint: string) => void;
}

function GateControl({
  gate,
  answers,
  setAnswer,
  testIdPrefix,
}: Readonly<{
  gate: WizardGate;
  answers: AnswerMap;
  setAnswer: (id: string, value: unknown) => void;
  testIdPrefix: string;
}>) {
  const gateError = React.useContext(FieldErrorContext)(`${testIdPrefix}-${gate.id}`);
  if (gate.type === 'boolean') {
    const value = answers[gate.id];
    return (
      <fieldset className="border-0 p-0 m-0 mb-md">
        <legend className="font-sans text-[15px] font-medium text-black mb-sm p-0">
          {gate.label}
        </legend>
        <div className={`flex flex-col gap-sm ${WIZARD_RADIO_OVERRIDE}`}>
          <Radio
            label="No"
            checked={value === false}
            onChange={() => setAnswer(gate.id, false)}
            data-testid={`${testIdPrefix}-${gate.id}-no`}
          />
          <Radio
            label="Yes"
            checked={value === true}
            onChange={() => setAnswer(gate.id, true)}
            data-testid={`${testIdPrefix}-${gate.id}-yes`}
          />
        </div>
        <FieldErrorMessage testId={`${testIdPrefix}-${gate.id}`} message={gateError} />
      </fieldset>
    );
  }
  const selected = Array.isArray(answers[gate.id]) ? (answers[gate.id] as string[]) : [];
  // Standardized look (squircle checkboxes): Other Income lays its options out
  // 3-per-row (gridded); Protection / Other Advisors keep a single flex-wrap row.
  const gridded = gate.id === OTHER_INCOME_GATE_ID;
  const standardized = gridded || gate.id === PROTECTION_GATE_ID || gate.id === OTHER_ADVISORS_GATE_ID;
  // Other Advisors' gate label duplicates the section title, so it's suppressed and
  // the section title (mb-lg above) is the single heading; the options→inputs gap
  // is also 24px.
  const isOtherAdvisors = gate.id === OTHER_ADVISORS_GATE_ID;
  return (
    <fieldset className={`border-0 p-0 m-0 ${isOtherAdvisors ? 'mb-lg' : 'mb-md'}`}>
      {!isOtherAdvisors && (
        <legend
          className={
            standardized
              ? "font-sans text-body-02 font-medium! leading-normal text-black mb-md p-0"
              : "font-sans text-[15px] font-medium text-black mb-sm p-0"
          }
        >
          {gate.label}
        </legend>
      )}
      <div
        className={
          gridded
            ? `grid grid-cols-1 md:grid-cols-3 gap-x-lg gap-y-lg ${WIZARD_SQUIRCLE_CHECKBOX_OVERRIDE}`
            : standardized
              ? `flex flex-wrap gap-x-lg gap-y-sm ${WIZARD_SQUIRCLE_CHECKBOX_OVERRIDE}`
              : 'flex flex-wrap gap-x-lg gap-y-sm'
        }
      >
        {(gate.options ?? []).map((opt) => (
          <Checkbox
            key={opt.value}
            label={opt.label ?? opt.value}
            checked={selected.includes(opt.value)}
            onChange={() => setAnswer(gate.id, toggleGateOption(gate, selected, opt.value))}
            data-testid={`${testIdPrefix}-${gate.id}-${opt.value}`}
          />
        ))}
      </div>
      <FieldErrorMessage testId={`${testIdPrefix}-${gate.id}`} message={gateError} />
    </fieldset>
  );
}

function FieldGrid({
  fields,
  answers,
  setAnswer,
  clients,
  testIdPrefix,
  renderDocument,
  renderOptionsSelect,
}: Readonly<{
  fields: WizardField[];
  answers: AnswerMap;
  setAnswer: (id: string, value: unknown) => void;
  clients: ClientOption[];
  testIdPrefix: string;
  renderDocument?: RenderDocumentSlot;
  renderOptionsSelect?: RenderOptionsSelectSlot;
}>) {
  const showAdvisorOnly = React.useContext(AdvisorOnlyVisibilityContext);
  const investedFields = fields.filter((f) => PENSIONS_INVESTED_FIELD_IDS.includes(f.id));
  let investedRendered = false;
  // items-start top-anchors the cells so a per-field error message extends ONLY its
  // own cell downward: CSS-grid's equal per-row height then makes the row sibling
  // grow too (empty space below its input = the "compensation"), keeping the two
  // inputs aligned and the next row unbroken. Single/full-width fields render their
  // message below with no sibling to compensate.
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 items-start gap-x-lg gap-y-lg">
      {fields.map((f) => {
        // Advisor-only field (e.g. "This policy is in agency") — hidden in the
        // client wizard so the whole "For Advisor Only" block disappears.
        if (!showAdvisorOnly && f.advisorOnly) return null;
        if (investedFields.length > 0 && PENSIONS_INVESTED_FIELD_IDS.includes(f.id)) {
          if (investedRendered) return null;
          investedRendered = true;
          return (
            <fieldset key="pensions-invested" className="lg:col-span-2 border-0 p-0 m-0">
              <legend className="p-0 mb-md font-sans text-body-02 font-medium leading-normal text-black">
                {PENSIONS_INVESTED_HEADING}
              </legend>
              <div className={`flex flex-wrap gap-x-lg gap-y-sm ${WIZARD_SQUIRCLE_CHECKBOX_OVERRIDE}`}>
                {investedFields.map((bf) => (
                  <WizardFieldInput
                    key={bf.id}
                    field={bf}
                    value={answers[bf.id]}
                    onChange={(v) => setAnswer(bf.id, v)}
                    clients={clients}
                    testIdPrefix={testIdPrefix}
                    groupAnswers={answers}
                    renderDocument={renderDocument}
                    renderOptionsSelect={renderOptionsSelect}
                  />
                ))}
              </div>
            </fieldset>
          );
        }
        // Pensions "This policy is in agency" → rendered under a "For Advisor Only"
        // heading spanning the row (visual grouping).
        if (f.id === PENSIONS_IN_AGENCY_FIELD_ID) {
          return (
            <fieldset key={f.id} className="lg:col-span-2 border-0 p-0 m-0">
              <legend className="p-0 mb-sm font-sans text-body-02 font-normal leading-normal text-black">
                {FOR_ADVISOR_ONLY_LABEL}
              </legend>
              <div className={`flex flex-wrap gap-x-lg gap-y-sm ${WIZARD_SQUIRCLE_CHECKBOX_OVERRIDE}`}>
                <WizardFieldInput
                  field={f}
                  value={answers[f.id]}
                  onChange={(v) => setAnswer(f.id, v)}
                  clients={clients}
                  testIdPrefix={testIdPrefix}
                  groupAnswers={answers}
                  renderDocument={renderDocument}
                  renderOptionsSelect={renderOptionsSelect}
                />
              </div>
            </fieldset>
          );
        }
        const input = (
          <WizardFieldInput
            field={f}
            value={answers[f.id]}
            onChange={(v) => setAnswer(f.id, v)}
            clients={clients}
            testIdPrefix={testIdPrefix}
            groupAnswers={answers}
            renderDocument={renderDocument}
            renderOptionsSelect={renderOptionsSelect}
          />
        );
        return f.display === 'radio' || f.type === 'document' || f.fullWidth ? (
          <div key={f.id} className="lg:col-span-2">
            {input}
          </div>
        ) : (
          <React.Fragment key={f.id}>{input}</React.Fragment>
        );
      })}
    </div>
  );
}

function EmploymentItemFields({
  fields,
  answers,
  setAnswer,
  clients,
  testIdPrefix,
  renderDocument,
  renderOptionsSelect,
}: Readonly<{
  fields: WizardField[];
  answers: AnswerMap;
  setAnswer: (id: string, value: unknown) => void;
  clients: ClientOption[];
  testIdPrefix: string;
  renderDocument?: RenderDocumentSlot;
  renderOptionsSelect?: RenderOptionsSelectSlot;
}>) {
  // `fields` is already visibility-filtered (showIf / followUps applied), so when
  // the employment type isn't one of Employed/Self-Employed/Company Director the
  // type-gated fields + toggles are already gone, leaving just `employmentType`.
  const owner = fields.find((f) => f.type === 'clientRef');
  const toggles = fields.filter((f) => f.type === 'boolean');
  const toggleIds = new Set(toggles.map((f) => f.id));
  // An "extras amount" (Bonus/Share-options/BIK value) reveals off a toggle, i.e.
  // its showIf references a field OTHER than the employment-type gate. The
  // type-gated main fields (job title / employer / base salary) showIf on
  // `employmentType`, so they stay in the main grid right after it.
  const isExtrasAmount = (f: WizardField) =>
    !!f.showIf && f.showIf.field !== EMPLOYMENT_TYPE_FIELD_ID;
  const mainFields = fields.filter(
    (f) => f !== owner && f.type !== 'boolean' && !isExtrasAmount(f),
  );
  // Only show extras amounts whose controlling toggle is itself visible, so a
  // stale toggle value can't leak its amount once the type gate closes.
  const revealed = fields.filter(
    (f) => isExtrasAmount(f) && f.showIf != null && toggleIds.has(f.showIf.field),
  );

  return (
    <div className="flex flex-col gap-lg">
      {owner && (
        <div className="flex flex-col gap-md">
          <span className={WIZARD_SUBLABEL_CLASS}>{owner.label ?? owner.id}</span>
          <div className={`flex flex-wrap gap-x-lg gap-y-sm ${WIZARD_RADIO_OVERRIDE}`}>
            {clients.map((c) => (
              <Radio
                key={c.id}
                label={c.name}
                checked={answers[owner.id] === c.id}
                onChange={() => setAnswer(owner.id, c.id)}
                data-testid={`${testIdPrefix}-${owner.id}-${c.id}`}
              />
            ))}
          </div>
        </div>
      )}

      {mainFields.length > 0 && (
        <FieldGrid
          fields={mainFields}
          answers={answers}
          setAnswer={setAnswer}
          clients={clients}
          testIdPrefix={testIdPrefix}
          renderDocument={renderDocument}
          renderOptionsSelect={renderOptionsSelect}
        />
      )}

      {toggles.length > 0 && (
        <div className="flex flex-col gap-md">
          <span className={WIZARD_SUBLABEL_CLASS}>{EMPLOYMENT_EXTRAS_LABEL}</span>
          <div
            className={`flex flex-wrap gap-x-lg gap-y-sm ${WIZARD_SQUIRCLE_CHECKBOX_OVERRIDE}`}
          >
            {toggles.map((f) => (
              <Checkbox
                key={f.id}
                label={f.label ?? f.id}
                checked={answers[f.id] === true}
                onChange={(e) => setAnswer(f.id, e.target.checked)}
                data-testid={`${testIdPrefix}-${f.id}`}
              />
            ))}
          </div>
        </div>
      )}

      {revealed.length > 0 && (
        <FieldGrid
          fields={revealed}
          answers={answers}
          setAnswer={setAnswer}
          clients={clients}
          testIdPrefix={testIdPrefix}
          renderDocument={renderDocument}
          renderOptionsSelect={renderOptionsSelect}
        />
      )}
    </div>
  );
}

/**
 * Per-card "Save Details" config (Dependants & Family). When present, each
 * repeating card renders a Save Details button next to Remove; `canSave` gates
 * on the whole group, `onSave` persists the whole section.
 */
type PerItemSave = {
  canSave: boolean;
  saving: boolean;
  saveLabel: string;
  /** Persists the whole section; the index identifies which card to collapse on success. */
  onSave: (index: number) => void;
};

function RepeatingGroup({
  group,
  items,
  onChange,
  clients,
  testIdPrefix,
  renderDocument,
  renderOptionsSelect,
  whiteCard = false,
  perItemSave,
  isCollapsed,
  onToggleCollapse,
  soloClientId,
}: Readonly<{
  group: WizardGroup;
  items: AnswerMap[];
  onChange: (items: AnswerMap[]) => void;
  clients: ClientOption[];
  testIdPrefix: string;
  renderDocument?: RenderDocumentSlot;
  renderOptionsSelect?: RenderOptionsSelectSlot;
  whiteCard?: boolean;
  perItemSave?: PerItemSave;
  isCollapsed?: (index: number) => boolean;
  onToggleCollapse?: (index: number) => void;
  soloClientId?: string;
}>) {
  const updateItem = (index: number, patch: AnswerMap) => {
    onChange(items.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };
  return (
    <div className="flex flex-col gap-lg" data-testid={`${testIdPrefix}-${group.id}`}>
      {items.map((item, index) => (
        // Index keys are safe here: rows are append/remove-only working state.
        <RepeatingItem
          key={index}
          group={group}
          item={item}
          index={index}
          clients={clients}
          testIdPrefix={`${testIdPrefix}-${group.id}-${index}`}
          removeTestId={`${testIdPrefix}-${group.id}-remove-${index}`}
          renderDocument={renderDocument}
          renderOptionsSelect={renderOptionsSelect}
          whiteCard={whiteCard}
          perItemSave={perItemSave}
          collapsed={isCollapsed?.(index) ?? false}
          onToggleCollapse={() => onToggleCollapse?.(index)}
          onPatch={(patch) => updateItem(index, patch)}
          onReplace={(next) => onChange(items.map((it, i) => (i === index ? next : it)))}
          onRemove={() => onChange(items.filter((_, i) => i !== index))}
          soloClientId={soloClientId}
        />
      ))}
      <div>
        <button
          type="button"
          className="bg-transparent border-0 cursor-pointer flex items-center gap-sm text-black text-body-02 font-medium leading-normal capitalize font-sans hover:underline"
          onClick={() => onChange([...items, {}])}
          data-testid={`${testIdPrefix}-${group.id}-add`}
        >
          {/* Icon inherits the button's #141414 via currentColor. */}
          <AppIcon name="figma plus square" size={16} /> {group.addLabel ?? 'Add Another'}
        </button>
      </div>
    </div>
  );
}

/**
 * One card of a repeating group. Seeds any visible field `default`s (e.g.
 * Investments' Currency → EUR) into the item once they're revealed: the patch is
 * merged for display so the control opens pre-selected, and persisted so the
 * required-field gate and submit see the value.
 */
function RepeatingItem({
  group,
  item,
  index,
  clients,
  testIdPrefix,
  removeTestId,
  renderDocument,
  renderOptionsSelect,
  whiteCard = false,
  perItemSave,
  collapsed = false,
  onToggleCollapse,
  onPatch,
  onReplace,
  onRemove,
  soloClientId,
}: Readonly<{
  group: WizardGroup;
  item: AnswerMap;
  index: number;
  clients: ClientOption[];
  testIdPrefix: string;
  removeTestId: string;
  renderDocument?: RenderDocumentSlot;
  renderOptionsSelect?: RenderOptionsSelectSlot;
  whiteCard?: boolean;
  perItemSave?: PerItemSave;
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  // Partial merge — used only to seed missing defaults (never deletes keys).
  onPatch: (patch: AnswerMap) => void;
  // Full replace — a field change recomputes the whole item (via applyFieldChange)
  // so values a changed followUp/showIf now hides are pruned, not merged.
  onReplace: (next: AnswerMap) => void;
  onRemove: () => void;
  soloClientId?: string;
}>) {
  const defaults = pendingDefaults(group.fields, item, REVEAL_ALL);
  const displayItem = Object.keys(defaults).length > 0 ? { ...defaults, ...item } : item;

  useEffect(() => {
    if (Object.keys(defaults).length > 0) onPatch(defaults);
    // Re-runs whenever the item changes; the missing-only patch converges (once
    // seeded the value is present, so no further patch is produced).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, group.fields]);

  const fields = visibleFields(group.fields, displayItem, REVEAL_ALL, soloClientId);
  // Replace the whole item so a followUp/showIf value the change now hides is
  // dropped (e.g. re-selecting a Child's "Life stage" resets "How much will fees
  // cost"); a plain merge would leave the stale value behind. `_ref` + other
  // non-field keys are preserved by applyFieldChange.
  const setAnswer = (id: string, value: unknown) =>
    onReplace(applyFieldChange(group.fields, displayItem, id, value, REVEAL_ALL));
  const providerLogos = React.useContext(ProviderLogosContext);

  // Client per-card cards (whiteCard + a per-card Save) are collapsible: a saved
  // card collapses to a summary, and the chevron toggles it open/closed anytime.
  const collapsible = whiteCard && !!perItemSave;

  if (collapsible && collapsed) {
    const summary = summarizeItem(group, displayItem, clients);
    // Left avatar: the provider's logo when its name resolves in the catalogue,
    // else an initials monogram (so every closed card shows a consistent avatar).
    const logoUrl = summary.providerName ? providerLogos[summary.providerName] : undefined;
    return (
      <div className="flex items-center gap-lg self-stretch rounded-lg border border-purple-primary bg-white p-md">
        <ProviderLogo
          logoUrl={logoUrl}
          name={summary.providerName ?? summary.title}
          size={48}
          testId={`${testIdPrefix}-logo`}
        />
        <button
          type="button"
          className="flex flex-1 min-w-0 items-center justify-between gap-md bg-transparent border-0 cursor-pointer focus:outline-none text-left"
          onClick={onToggleCollapse}
          data-testid={`${testIdPrefix}-toggle`}
          aria-expanded={false}
        >
          <span className="flex flex-col gap-xs min-w-0">
            <span className="font-sans text-[18px] font-medium leading-normal text-black">
              {summary.title}
            </span>
            {summary.parts.length > 0 && (
              <span className="flex items-center gap-sm">
                {summary.parts.map((part, i) => (
                  <React.Fragment key={`${part}-${i}`}>
                    {i > 0 && (
                      <span
                        aria-hidden
                        className="inline-block w-[6px] h-[6px] rounded-full bg-grey-04 shrink-0"
                      />
                    )}
                    <span className="font-sans text-body-02 font-normal leading-normal text-black">
                      {part}
                    </span>
                  </React.Fragment>
                ))}
              </span>
            )}
          </span>
          <span className="flex items-center gap-md shrink-0">
            {/* Solo relationship: the owner is the only client — omit the redundant
                owner badge (the picker is hidden too). */}
            {!soloClientId && summary.owner && (
              <span
                data-testid={`${testIdPrefix}-owner`}
                className="flex items-center justify-center px-sm py-xs rounded bg-neutral-bg font-sans text-body-03 font-medium text-neutral-text"
              >
                {summary.owner}
              </span>
            )}
            <span aria-hidden className="text-black leading-[0]">
              <AppIcon name="chevron compact down" size={24} />
            </span>
          </span>
        </button>
      </div>
    );
  }

  return (
    <div
      className={
        whiteCard
          ? 'bg-white border border-purple-primary rounded-lg p-md'
          : 'bg-page-bg rounded-lg p-md'
      }
    >
      {/* No chevron while a card is open/expanded — it only renders on the
          collapsed summary header (so a saved card can be re-opened). A card
          closes by clicking its "Save Details" (which is gated on the card being
          complete), never via a manual collapse-while-open control. */}
      {group.id === EMPLOYMENT_GROUP_ID ? (
        <EmploymentItemFields
          fields={fields}
          answers={displayItem}
          setAnswer={setAnswer}
          clients={clients}
          testIdPrefix={testIdPrefix}
          renderDocument={renderDocument}
          renderOptionsSelect={renderOptionsSelect}
        />
      ) : (
        <FieldGrid
          fields={fields}
          answers={displayItem}
          setAnswer={setAnswer}
          clients={clients}
          testIdPrefix={testIdPrefix}
          renderDocument={renderDocument}
          renderOptionsSelect={renderOptionsSelect}
        />
      )}
      {(group.subGroups ?? [])
        .filter((sub) => showIfSatisfied(sub, displayItem))
        .map((sub) => {
          const subGate = gateState(sub.gate, displayItem);
          const subItems = Array.isArray(displayItem[sub.id]) ? (displayItem[sub.id] as AnswerMap[]) : [{}];
          return (
            <div key={sub.id}>
              <div className="py-lg">
                <div className="h-px w-full bg-grey-05" />
              </div>
              {sub.gate && (
                <GateControl
                  gate={sub.gate}
                  answers={displayItem}
                  setAnswer={(id, value) => onReplace({ ...displayItem, [id]: value })}
                  testIdPrefix={testIdPrefix}
                />
              )}
              {(!sub.gate || subGate.open) && (
                <RepeatingGroup
                  group={sub}
                  items={subItems}
                  onChange={(items) => onReplace({ ...displayItem, [sub.id]: items })}
                  clients={clients}
                  testIdPrefix={`${testIdPrefix}-${sub.id}`}
                  renderDocument={renderDocument}
                  renderOptionsSelect={renderOptionsSelect}
                  whiteCard
                  perItemSave={{
                    canSave: isGroupComplete(sub, displayItem),
                    saving: perItemSave?.saving ?? false,
                    saveLabel: perItemSave?.saveLabel ?? 'Save Details',
                    onSave: () => perItemSave?.onSave(index),
                  }}
                  soloClientId={soloClientId}
                />
              )}
            </div>
          );
        })}
      <div
        className={`flex items-center mt-lg ${
          perItemSave ? 'justify-end gap-md' : 'justify-start'
        }`}
      >
        {perItemSave ? (
          // Outlined purple pill (matches the filled Save Details beside it), with
          // the design-system hover. Used in the Dependants & Family cards.
          <Button
            type="button"
            variant="outlined"
            colour="purple"
            onClick={onRemove}
            data-testid={removeTestId}
          >
            Remove
          </Button>
        ) : (
          <button
            type="button"
            className="bg-transparent border-0 cursor-pointer flex items-center gap-sm text-black text-body-02 font-medium leading-normal capitalize font-sans"
            onClick={onRemove}
            data-testid={removeTestId}
          >
            {/* Icon inherits the button's #141414 via currentColor. */}
            <AppIcon name="trash" size={16} /> Remove
          </button>
        )}
        {perItemSave && (
          // Save Details sits inside the card next to Remove (per design); it
          // gates on the whole group but persists the whole section.
          <Button
            variant="filled"
            colour="purple"
            loading={perItemSave.saving}
            disabled={perItemSave.saving || !perItemSave.canSave}
            onClick={() => perItemSave.onSave(index)}
            data-testid={`${testIdPrefix}-save`}
          >
            {perItemSave.saveLabel}
          </Button>
        )}
      </div>
    </div>
  );
}

const WizardSectionFormInner: React.FC<WizardSectionFormProps> = ({
  section,
  clients,
  initialAnswers,
  saving,
  errors,
  onSave,
  renderIntegration,
  renderDocument,
  renderOptionsSelect,
  renderChat,
  saveLabel = 'Save Details',
  hideChrome = false,
  startCollapsed = false,
  onDirtyChange,
  workingAnswers,
  onAnswersChange,
  externalBaseline,
  onBaselineEstablished,
  lockedFields,
  hideSoloOwnerFields = false,
}) => {
  // SOLO relationship (client wizard only): the sole client owns everything, so
  // the `clientRef` owner pickers are hidden and auto-seeded. `undefined` for a
  // joint relationship or the advisor accordion → every clientRef behaves as today.
  const soloClientId =
    hideSoloOwnerFields && clients.length === 1 ? clients[0].id : undefined;

  // Per-field error resolver: a field's `data-testid` mirrors the server's dotted
  // `questionId` (`.`/`[i]` ↔ `-`), so any field can look up its own message from
  // FieldErrorContext by its testid — red border + message below, no prop threading.
  const lookupFieldError = React.useCallback(
    (testId: string): string | undefined => {
      for (const e of errors) {
        const key = `wz-${e.questionId.replace(/\[(\d+)\]/g, '-$1').replace(/\./g, '-')}`;
        if (key === testId) return e.message;
      }
      return undefined;
    },
    [errors],
  );
  // Errors that attach to no input (synthetic `_save` / server `$section`) — shown
  // near the save button so a save failure stays visible even without a field.
  const unmatchedErrors = errors.filter(
    (e) => e.questionId === '_save' || e.questionId === '$section',
  );
  // Seed from the host-preserved working buffer when supplied (restores unsaved
  // edits after a step-switch remount), else from the saved server slice. Read
  // once — later updates flow in via the host re-seeding on the next remount.
  //
  // Derived/locked fields are the exception: their value is owned by ANOTHER
  // section (cross-section prefill), so they always take the fresh seed from
  // `initialAnswers`, never a value the working buffer captured on an earlier
  // visit — before the source was filled in. Without this, entering Expenses
  // once (empty Dependants) buffers an empty "Education Costs", and returning
  // after entering dependants' fees would keep showing it blank even though the
  // field is (correctly) locked. The user can't edit a locked field here, so
  // overwriting the buffered value with the seed is always safe.
  const [answers, setAnswers] = useState<AnswerMap>(() => {
    const base = workingAnswers ?? initialAnswers ?? {};
    if (!workingAnswers || !lockedFields || lockedFields.size === 0) return base;
    const merged = { ...base };
    for (const id of lockedFields) {
      if (initialAnswers && id in initialAnswers) merged[id] = initialAnswers[id];
      else delete merged[id];
    }
    return merged;
  });
  // CHAT sections start in chat mode when a chat renderer is supplied; "Answer
  // as a form instead" (or no renderer) drops to the fallback form.
  const [chatMode, setChatMode] = useState(
    section.kind === 'CHAT' && !!section.chat && !!renderChat,
  );
  const testIdPrefix = `wz-${section.id}`;
  const setAnswer = (id: string, value: unknown) => setAnswers((a) => ({ ...a, [id]: value }));

  // --- Per-card collapse (client per-card-save sections) -------------------
  // A saved card collapses to a summary; the collapsed summary's chevron re-opens
  // it. Keyed `${groupId}:${index}` (repeating cards) / `byClient:${clientId}`
  // (Personal Details). Every card/block ALWAYS opens EXPANDED — the only thing
  // that collapses one is pressing its "Save Details" (the collapse-on-save effect
  // below); a card is never collapsed just because its answers already have data
  // (incl. prefilled Personal Details) or because the page was revisited.
  const [collapsedItems, setCollapsedItems] = useState<Set<string>>(() => new Set());
  // Whole-section collapse (gate-only / field-grid steps that save at the section
  // level, e.g. Dependants = "No", Expenses, Other Advisors). Set on a successful
  // section-level save; reset to expanded on (re)mount and when re-opened.
  const [sectionCollapsed, setSectionCollapsed] = useState(false);
  const pendingSaveKeyRef = useRef<string | null>(null);
  const prevSavingRef = useRef(saving);
  useEffect(() => {
    if (prevSavingRef.current && !saving) {
      if (pendingSaveKeyRef.current && errors.length === 0) {
        const key = pendingSaveKeyRef.current;
        if (key === PER_CLIENT_SAVE_KEY) {
          // PER_CLIENT section saves collapse every client block at once — but only
          // when there are 2+ clients (2+ blocks to tidy away, e.g. Personal Details
          // for a couple). With a single client there's just one block, so collapsing
          // it adds nothing: leave it expanded in place.
          if (clients.length > 1) {
            setCollapsedItems((s) => {
              const next = new Set(s);
              clients.forEach((c) => next.add(`byClient:${c.id}`));
              return next;
            });
          }
        } else if (key === SECTION_SAVE_KEY) {
          // Section-level save collapses the whole section to its title — but only
          // for a JOINT relationship (2+ clients), mirroring the PER_CLIENT gate
          // above. A solo relationship keeps its main container expanded (same as
          // Personal Details solo): saving never hides the section behind a title.
          if (clients.length > 1) {
            setSectionCollapsed(true);
          }
        } else {
          setCollapsedItems((s) => new Set(s).add(key));
        }
      }
      pendingSaveKeyRef.current = null;
    }
    prevSavingRef.current = saving;
  }, [saving, errors, clients]);
  const toggleCollapse = (key: string) =>
    setCollapsedItems((s) => {
      const next = new Set(s);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Solo relationship: seed the sole client as the owner of every visible
  // clientRef whose picker is hidden (see soloClientId / visibleFields). The
  // required owner must be present in the answers for "Save Details" to enable,
  // for the backend validator to accept the save, and for the financial-position
  // projectors to resolve the owner. Idempotent: soloOwnerSeed returns null once
  // every clientRef already carries the client id (`?? a` keeps the same reference
  // so React bails out — no loop), and re-runs when `answers` gains a new item.
  useEffect(() => {
    if (!soloClientId) return;
    setAnswers((a) => soloOwnerSeed(section, a, soloClientId) ?? a);
  }, [soloClientId, section, answers]);

  // --- Unsaved-changes tracking (opt-in via onDirtyChange) -----------------
  // `answers` is the working buffer; `baselineRef` holds the fingerprint of the
  // last *saved* state. Dirty = the two diverge.
  const answersRef = useRef(answers);
  answersRef.current = answers;

  // Mirror the live working answers to the host so the Client wizard can preserve
  // them across a step-switch remount. Fires on mount (incl. default-seeding) and
  // every change; the host stores them per-section and feeds them back as
  // `workingAnswers`. No render loop: the host re-render keeps the same `key`, so
  // this editor is not remounted and `answers` identity is unchanged.
  useEffect(() => {
    onAnswersChange?.(answers);
  }, [answers, onAnswersChange]);

  const baselineRef = useRef<string | null>(null);
  // A stable fingerprint of the saved answers — its VALUE (not the prop's object
  // identity, which churns when the slice is `?? {}`) drives the re-baseline.
  const savedFingerprint = useMemo(
    () => sectionAnswersFingerprint(initialAnswers ?? {}),
    [initialAnswers],
  );

  // Establish the "saved" baseline. When the host supplies one (`externalBaseline`
  // — a step-switch remount that restored unsaved edits via `workingAnswers`),
  // adopt it directly so the restored edits read as dirty against the server
  // rather than against themselves. Otherwise self-establish from the SETTLED
  // answers — deferred so mount-time default seeding (e.g. a blank repeating card
  // seeding currency:EUR) doesn't read as a user edit — and report it up so the
  // host can feed it back on the next remount. Also re-runs after each save (the
  // saved answers, hence savedFingerprint, change). Until the baseline exists the
  // section is reported clean.
  useEffect(() => {
    if (!onDirtyChange && !onBaselineEstablished) return undefined;
    if (externalBaseline != null) {
      baselineRef.current = externalBaseline;
      onDirtyChange?.(sectionAnswersFingerprint(answersRef.current) !== externalBaseline);
      return undefined;
    }
    const timer = setTimeout(() => {
      const fingerprint = sectionAnswersFingerprint(answersRef.current);
      baselineRef.current = fingerprint;
      onDirtyChange?.(false);
      onBaselineEstablished?.(fingerprint);
    }, 0);
    return () => clearTimeout(timer);
  }, [savedFingerprint, externalBaseline, onDirtyChange, onBaselineEstablished]);

  // Report unsaved edits whenever the live answers diverge from the baseline.
  useEffect(() => {
    if (!onDirtyChange || baselineRef.current === null) return;
    onDirtyChange(sectionAnswersFingerprint(answers) !== baselineRef.current);
  }, [answers, onDirtyChange]);

  // Releasing the section (unmount / switching steps) clears the guard.
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);
  // -------------------------------------------------------------------------

  const sectionGate = gateState(section.gate, answers);
  // Section-level field setter: replace-and-prune (via applyFieldChange) so a
  // now-hidden followUp/showIf value is dropped, not merged. Kept separate from
  // the generic `setAnswer` above, which also sets gate/group/byClient keys that
  // must NOT be pruned as if they were section fields.
  const setSectionField = (id: string, value: unknown) =>
    setAnswers((a) => applyFieldChange(section.fields ?? [], a, id, value, sectionGate.revealed));
  const isChat = section.kind === 'CHAT';
  const perClient = section.layout === 'PER_CLIENT';
  // Each sub-section of a step saves INDEPENDENTLY (e.g. Property's "Your living
  // situation" vs "Investment properties"):
  //  • `sectionComplete` — the whole-step "done" save (section-level fields + every
  //    revealed group); used only where the step is a single sub-section.
  //  • `sectionFieldsReady` — the section-level ("living situation") fields ONLY,
  //    independent of any group; gates the Property Own-home / living-situation save.
  //  • a repeating-group card gates on its OWN group (`perItemSave.canSave` below).
  // The server validates the section leniently (optional gates / no minItems), so a
  // sub-section can save while the others are still empty.
  const sectionComplete = isSectionComplete(
    section,
    answers,
    clients.map((c) => c.id),
  );
  const sectionFieldsReady = areSectionFieldsComplete(section, answers);
  // Dependants & Family, Current Employment and Other Income render a Save
  // Details + Remove inside each white card (per repeating item) instead of a
  // single section-level button, each gated on its own group. Only the Client UI
  // wizard chrome (the advisor accordion keeps the single save), and only while a
  // repeating group is actually visible — so a closed boolean gate ("No") or an
  // empty/"None of these" multiselect keeps the single section save to persist
  // the gate-only answer.
  const hasVisibleRepeatingGroup = (section.groups ?? []).some((g) => {
    if (!g.repeating) return false;
    if (!(isGroupRevealed(g, sectionGate) || g.alwaysVisible)) return false;
    // A group with its own gate only shows its cards when that gate is open
    // (e.g. Property's investmentProperties / Dependants' familyMembers).
    if (g.gate && !gateState(g.gate, answers).open) return false;
    // An emptied group (the last card was removed → `[]`) no longer hosts a
    // per-card Save, so fall back to the section-level "Save Details" — otherwise
    // the removal has no Save button and can't be persisted. A freshly-revealed,
    // not-yet-touched group (still `undefined`; RepeatingGroup renders one blank
    // card) keeps the per-card save.
    const items = answers[g.id];
    return Array.isArray(items) ? items.length > 0 : true;
  });
  const perGroupSave =
    !hideChrome &&
    PER_GROUP_SAVE_SECTION_IDS.has(section.id) &&
    sectionGate.open &&
    hasVisibleRepeatingGroup;
  // Protection's "New protection policies" block owns its own Save Details footer (it is
  // always-visible and non-repeating, so the per-card save above doesn't cover it). When
  // present it also REPLACES the generic section-level save for Protection, so there is
  // never a duplicate "Save Details" — the block's button persists the whole section.
  const protectionNewPolicySave =
    !hideChrome &&
    section.id === PROTECTION_SECTION_ID &&
    (section.groups ?? []).some((g) => g.id === PROTECTION_NEW_POLICY_GROUP_ID);
  // Property "Own home": the home details render as a single white card with its
  // OWN "Save Details" (like the Investment Property cards), so the generic
  // section-level save is suppressed for that branch.
  const propertyOwnHome =
    !hideChrome &&
    section.id === PROPERTY_SECTION_ID &&
    answers[PROPERTY_LIVING_SITUATION_FIELD_ID] === 'OWN_HOME';

  // Re-entering an already-saved section (the host sets `startCollapsed` when the
  // user goes back to a COMPLETE / partially-saved step): open every container
  // collapsed, so the step shows its tidy saved state rather than all forms
  // expanded. Mount-only (the editor remounts per step) + useLayoutEffect so the
  // collapse lands before paint (no expand→collapse flicker). A never-saved
  // section keeps `startCollapsed` false → opens expanded as before.
  useLayoutEffect(() => {
    if (!startCollapsed) return;
    if (perClient) {
      // Personal Details (one block per client) → collapse every client block.
      setCollapsedItems(new Set(clients.map((c) => `byClient:${c.id}`)));
      return;
    }
    const keys = new Set<string>();
    // Repeating-group cards (Dependants / Investment Properties / …) → one key per saved card.
    (section.groups ?? []).forEach((g) => {
      const items = answers[g.id];
      if (Array.isArray(items)) items.forEach((_, i) => keys.add(`${g.id}:${i}`));
    });
    // Property "Own home" details render as their own card.
    if (propertyOwnHome) keys.add(`${section.id}:home`);
    if (keys.size > 0) {
      setCollapsedItems(keys);
    } else if (clients.length > 1) {
      // No per-card / per-client / home container → a section-level-save step
      // (e.g. Expenses, Other Advisors, a "No" gate): collapse the whole section
      // to its title, mirroring what its "Save Details" does. Joint only — a solo
      // relationship keeps its main container expanded (like Personal Details solo).
      setSectionCollapsed(true);
    }
    // Mount-only: seed from the saved state captured at entry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isChat && chatMode && renderChat) {
    return (
      <div
        data-testid={`wizard-section-${section.id}`}
        className="bg-white rounded-lg px-md py-lg shadow-card"
      >
        <h2 className="m-0 font-sans text-body-01 font-medium text-black text-center">
          {section.title}
        </h2>
        {renderChat({
          section,
          saving,
          onComplete: (chatAnswers) => onSave(cleanSectionAnswers(chatAnswers)),
          onUseFormInstead: () => setChatMode(false),
        })}
        {unmatchedErrors.length > 0 && (
          <div
            data-testid={`${testIdPrefix}-errors`}
            className="mt-md p-sm rounded-input bg-error-bg text-body-03 font-sans font-normal text-[var(--input-error-text)]"
          >
            {unmatchedErrors.map((e) => (
              <div key={`${e.questionId}-${e.message}`}>{e.message}</div>
            ))}
          </div>
        )}
      </div>
    );
  }

  const renderGroup = (group: WizardGroup, index: number) => {
    const groupGate = gateState(group.gate, answers);
    const body = group.repeating ? (
      <RepeatingGroup
        group={group}
        items={Array.isArray(answers[group.id]) ? (answers[group.id] as AnswerMap[]) : [{}]}
        onChange={(items) => setAnswer(group.id, items)}
        clients={clients}
        testIdPrefix={testIdPrefix}
        renderDocument={renderDocument}
        renderOptionsSelect={renderOptionsSelect}
        whiteCard={perGroupSave}
        soloClientId={soloClientId}
        isCollapsed={(i) => collapsedItems.has(`${group.id}:${i}`)}
        onToggleCollapse={(i) => toggleCollapse(`${group.id}:${i}`)}
        perItemSave={
          perGroupSave
            ? {
                canSave:
                  isGroupComplete(group, answers),
                saving,
                saveLabel,
                onSave: (i) => {
                  // Remember which card to collapse once the save succeeds.
                  pendingSaveKeyRef.current = `${group.id}:${i}`;
                  onSave(cleanSectionAnswers(answers));
                },
              }
            : undefined
        }
      />
    ) : (
      <FieldGrid
        fields={visibleFields(
          group.fields,
          (answers[group.id] as AnswerMap) ?? {},
          new Set(['*']),
          soloClientId,
        )}
        answers={(answers[group.id] as AnswerMap) ?? {}}
        setAnswer={(id, v) =>
          // Replace the group's answer object (not merge) so a now-hidden
          // followUp/showIf value is pruned rather than lingering.
          setAnswer(
            group.id,
            applyFieldChange(group.fields, (answers[group.id] as AnswerMap) ?? {}, id, v, REVEAL_ALL),
          )
        }
        clients={clients}
        testIdPrefix={`${testIdPrefix}-${group.id}`}
        renderDocument={renderDocument}
        renderOptionsSelect={renderOptionsSelect}
      />
    );
    return (
      <section key={group.id}>
        {index > 0 && (
          <>
            {/* Full-width 1px divider with 24px breathing room above + below. */}
            <div className="py-lg">
              <div className="h-px w-full bg-grey-05" />
            </div>
            {group.title && (
              <h3 className="m-0 mb-lg font-sans text-body-01 font-medium leading-normal text-black">
                {group.title}
              </h3>
            )}
          </>
        )}
        {index === 0 &&
          (section.id === OTHER_INCOME_SECTION_ID || section.id === PROTECTION_SECTION_ID) &&
          group.title && (
          <>
            {/* First revealed gated group (Other Income / Protection): same 1px divider as
                the 2nd+ groups, with a 16px title→box gap (the later groups keep 24px). */}
            <div className="py-lg">
              <div className="h-px w-full bg-grey-05" />
            </div>
            <h3 className="m-0 mb-md font-sans text-body-01 font-medium leading-normal text-black">
              {group.title}
            </h3>
          </>
        )}
        {index === 0 &&
          section.id === PROPERTY_SECTION_ID &&
          group.id === PROPERTY_INVESTMENTS_GROUP_ID &&
          group.title && (
            <>
              {/* Property's investment group is index 0 (the living-situation fields
                  sit at the section level above), so it misses the index>0 divider.
                  Render the standardized divider + title explicitly. */}
              <div className="py-lg">
                <div className="h-px w-full bg-grey-05" />
              </div>
              <h3 className="m-0 mb-lg font-sans text-body-01 font-medium leading-normal text-black">
                {group.title}
              </h3>
            </>
          )}
        {group.id === EMPLOYMENT_GROUP_ID && (
          // Standalone heading sitting OUTSIDE the FAFAFA item card, 24px above it
          // (mirrors the Personal Details section title styling).
          <h3 className="m-0 mb-lg font-sans text-body-01 font-medium leading-normal text-black">
            {EMPLOYMENT_GROUP_TITLE}
          </h3>
        )}
        {group.gate && (
          <GateControl
            gate={group.gate}
            answers={answers}
            setAnswer={setAnswer}
            testIdPrefix={testIdPrefix}
          />
        )}
        {(!group.gate || groupGate.open) && body}
        {protectionNewPolicySave && group.id === PROTECTION_NEW_POLICY_GROUP_ID && (
          // The "New protection policies" block's own Save Details (design image 4). Shown
          // whether "interested?" is Yes or No, gated on its own required fields AND a
          // protectionTypes selection (the server requires the multiselect gate). Persists
          // the whole section, like every other Save Details in the wizard.
          <div className="flex justify-end mt-lg">
            <Button
              variant="filled"
              colour="purple"
              loading={saving}
              disabled={
                saving ||
                !(sectionGate.open && isGroupComplete(group, answers))
              }
              onClick={() => {
                // Protection's "New policies" save is the section's done-save →
                // collapse the whole section to its title on success.
                pendingSaveKeyRef.current = SECTION_SAVE_KEY;
                onSave(cleanSectionAnswers(answers), { advance: true });
              }}
              data-testid={`${testIdPrefix}-save`}
            >
              {saveLabel}
            </Button>
          </div>
        )}
      </section>
    );
  };

  const renderPerClient = () => {
    const byClient = (answers.byClient as Record<string, AnswerMap>) ?? {};
    const setClientAnswer = (clientId: string, id: string, value: unknown) =>
      setAnswers((a) => {
        const current = (a.byClient as Record<string, AnswerMap>) ?? {};
        // Replace this client's sub-map (not merge) so a now-hidden
        // followUp/showIf value is pruned rather than lingering.
        const nextForClient = applyFieldChange(
          section.fields ?? [],
          current[clientId] ?? {},
          id,
          value,
          REVEAL_ALL,
        );
        return { ...a, byClient: { ...current, [clientId]: nextForClient } };
      });
    return (
      <div className="flex flex-col gap-lg">
        {clients.map((client) => (
          <PerClientBlock
            key={client.id}
            client={client}
            sectionTitle={section.title}
            fields={section.fields ?? []}
            answers={byClient[client.id] ?? {}}
            setAnswer={(id, v) => setClientAnswer(client.id, id, v)}
            clients={clients}
            testIdPrefix={`${testIdPrefix}-${client.id}`}
            collapsible={!hideChrome}
            collapsed={collapsedItems.has(`byClient:${client.id}`)}
            onToggleCollapse={() => toggleCollapse(`byClient:${client.id}`)}
            renderDocument={renderDocument}
            renderOptionsSelect={renderOptionsSelect}
          />
        ))}
      </div>
    );
  };

  return (
    <FieldErrorContext.Provider value={lookupFieldError}>
    <div className={hideChrome || perClient ? '' : 'flex flex-col gap-md'}>
    <div
      data-testid={`wizard-section-${section.id}`}
      // PER_CLIENT sections render one card per client (see renderPerClient /
      // PerClientBlock), and a hideChrome host supplies its own container, so in
      // both cases the outer wrapper stays transparent; every other section is
      // itself a single card.
      className={
        hideChrome || perClient
          ? ''
          : 'bg-white rounded-lg px-md py-lg shadow-card'
      }
    >
      {!hideChrome && !perClient && sectionCollapsed ? (
        // Collapsed: just the section title + a chevron to re-open. The section
        // only collapses via its "Save Details" (never a manual control while open).
        <button
          type="button"
          className="w-full flex items-center justify-between bg-transparent border-0 cursor-pointer focus:outline-none"
          onClick={() => setSectionCollapsed(false)}
          data-testid={`${testIdPrefix}-section-toggle`}
          aria-expanded={false}
        >
          <span className="text-body-01 font-sans not-italic font-medium leading-normal text-black">
            {section.title}
          </span>
          <span aria-hidden className="text-black leading-[0]">
            <AppIcon name="chevron compact down" size={24} />
          </span>
        </button>
      ) : (
      <>
      {!hideChrome && !perClient && (
        <h2 className="flex lg:hidden text-body-01 font-sans not-italic font-medium leading-normal text-black mb-lg">
          {section.title}
        </h2>
      )}

      {/* Desktop-only standardized step title (mobile uses the lg:hidden h2 above, so
          each breakpoint shows the title exactly once). Opted-in per section. */}
      {!hideChrome && DESKTOP_TITLE_SECTION_IDS.includes(section.id) && (
        <h3 className="hidden lg:flex m-0 mb-lg font-sans text-body-01 font-medium leading-normal text-black">
          {section.title}
        </h3>
      )}

      {isChat && (
        <p className="text-body-03 text-[#6b7280] mb-[12px]" data-testid={`${testIdPrefix}-chat-note`}>
          A guided conversation for this section is coming soon — you can answer below in the
          meantime.
        </p>
      )}

      {/* "Auto-fill this section" banner (e.g. Open Banking on Expenses) sits just
          below the title, above the fields, per design. Only the Client UI supplies
          renderIntegration; the Advisor accordion omits it. */}
      {section.integration && (
        <div className="mb-lg">
          {renderIntegration?.(section.integration, (values) =>
            setAnswers((a) => (typeof values === 'function' ? values(a) : { ...a, ...values })),
          )}
        </div>
      )}

      {section.gate && (
        <GateControl
          gate={section.gate}
          answers={answers}
          setAnswer={setAnswer}
          testIdPrefix={testIdPrefix}
        />
      )}

      {!perClient && section.id === PROPERTY_SECTION_ID && (
        // The Property section's living-situation fields live at the section level
        // (no group wraps them), so render their sub-heading explicitly — mirroring
        // the hardcoded EMPLOYMENT group title.
        <h3 className="m-0 mb-lg font-sans text-body-01 font-medium leading-normal text-black">
          {PROPERTY_LIVING_SITUATION_TITLE}
        </h3>
      )}

      {perClient
        ? renderPerClient()
        : (sectionGate.open || !section.gate) &&
          (() => {
            const visible = visibleFields(
              section.fields ?? [],
              answers,
              sectionGate.revealed,
              soloClientId,
            ).map(
              (f) =>
                section.id === PROPERTY_SECTION_ID && f.id === PROPERTY_LIVING_SITUATION_FIELD_ID
                  ? { ...f, display: 'radio' as const }
                  : f,
            );
            const fieldGrid = (gridFields: WizardField[]) => (
              <FieldGrid
                fields={gridFields}
                answers={answers}
                setAnswer={setSectionField}
                clients={clients}
                testIdPrefix={testIdPrefix}
                renderDocument={renderDocument}
                renderOptionsSelect={renderOptionsSelect}
              />
            );
            if (section.id !== PROPERTY_SECTION_ID) return fieldGrid(visible);
            // "Own home" details render inside a white card — the same pattern as
            // the Investment Property card — rather than loose inline fields. The
            // `home*` fields are revealed by "Own home"; the living-situation radio
            // (+ the renting branch) stays above the card.
            const homeFields = visible.filter((f) => f.id.startsWith('home'));
            const baseFields = visible.filter((f) => !f.id.startsWith('home'));
            const homeKey = `${section.id}:home`;
            const homeCollapsed = collapsedItems.has(homeKey);
            return (
              <>
                {fieldGrid(baseFields)}
                {homeFields.length > 0 &&
                  (homeCollapsed ? (
                    // Collapsed: a summary header + chevron to re-open (saved card).
                    <div className="bg-white border border-purple-primary rounded-lg p-md mt-lg">
                      <button
                        type="button"
                        className="w-full flex items-center justify-between bg-transparent border-0 cursor-pointer focus:outline-none"
                        onClick={() => toggleCollapse(homeKey)}
                        data-testid={`${testIdPrefix}-home-toggle`}
                        aria-expanded={false}
                      >
                        <span className="font-sans text-body-02 font-medium leading-normal text-black">
                          Own home
                        </span>
                        <span aria-hidden className="text-black leading-[0]">
                          <AppIcon name="chevron compact down" size={24} />
                        </span>
                      </button>
                    </div>
                  ) : (
                    <div className="bg-white border border-purple-primary rounded-lg p-md mt-lg">
                      {fieldGrid(homeFields)}
                      {/* The home card owns its "Save Details" (like the Investment
                          Property cards). It persists the whole section and collapses
                          this card on success; the generic section save is suppressed
                          for Own home (see propertyOwnHome). */}
                      <div className="flex justify-end mt-lg">
                        <Button
                          variant="filled"
                          colour="purple"
                          loading={saving}
                          disabled={saving || !sectionFieldsReady}
                          onClick={() => {
                            pendingSaveKeyRef.current = homeKey;
                            onSave(cleanSectionAnswers(answers), { advance: true });
                          }}
                          data-testid={`${testIdPrefix}-home-save`}
                        >
                          {saveLabel}
                        </Button>
                      </div>
                    </div>
                  ))}
              </>
            );
          })()}

      {!perClient &&
        (section.groups ?? [])
          .filter((group) => isGroupRevealed(group, sectionGate) || group.alwaysVisible)
          .map((group, index) => renderGroup(group, index))}

      {/* Per-field validation errors now render on each input (red border + message
          below) via FieldErrorContext. Only field-less errors (a save failure /
          section-level `$section`) fall back to this inline note near the save. */}
      {unmatchedErrors.length > 0 && (
        <div
          data-testid={`${testIdPrefix}-errors`}
          className="mt-md font-sans text-body-03 font-normal text-[var(--input-error-text)]"
        >
          {unmatchedErrors.map((e) => (
            <div key={`${e.questionId}-${e.message}`}>{e.message}</div>
          ))}
        </div>
      )}

      {!perGroupSave && !protectionNewPolicySave && !propertyOwnHome && (
        <div className="flex justify-end mt-lg">
          <Button
            variant="filled"
            colour="purple"
            loading={saving}
            disabled={saving || !sectionComplete}
            onClick={() => {
              // On a successful save, collapse: PER_CLIENT → every client block;
              // any other section-level step → the whole section to its title.
              // (The only way to collapse — never while open.)
              pendingSaveKeyRef.current = perClient ? PER_CLIENT_SAVE_KEY : SECTION_SAVE_KEY;
              onSave(cleanSectionAnswers(answers), { advance: true });
            }}
            data-testid={`${testIdPrefix}-save`}
          >
            {saveLabel}
          </Button>
        </div>
      )}
      </>
      )}
    </div>
    </div>
    </FieldErrorContext.Provider>
  );
};

function PerClientBlock({
  client,
  sectionTitle,
  fields,
  answers,
  setAnswer,
  clients,
  testIdPrefix,
  collapsible,
  collapsed,
  onToggleCollapse,
  renderDocument,
  renderOptionsSelect,
}: Readonly<{
  client: ClientOption;
  sectionTitle: string;
  fields: WizardField[];
  answers: AnswerMap;
  setAnswer: (id: string, value: unknown) => void;
  clients: ClientOption[];
  testIdPrefix: string;
  collapsible: boolean;
  collapsed: boolean;
  onToggleCollapse: () => void;
  renderDocument?: RenderDocumentSlot;
  renderOptionsSelect?: RenderOptionsSelectSlot;
}>) {
  // Collapse is host-controlled (the section's collapsedItems): a block only
  // collapses via "Save Details", never a manual toggle while open.
  const expanded = collapsible ? !collapsed : true;

  if (!collapsible) {
    return (
      <section
        className="border border-grey-05 rounded-lg"
        data-testid={`${testIdPrefix}-block`}
      >
        <div className="px-md pt-[12px] pb-sm">
          <span className="font-sans text-[17px] font-medium text-black">
            {sectionTitle} - {client.name}
          </span>
        </div>
        <div className="px-md pb-md">
          <FieldGrid
            fields={visibleFields(fields, answers, new Set(['*']))}
            answers={answers}
            setAnswer={setAnswer}
            clients={clients}
            testIdPrefix={testIdPrefix}
            renderDocument={renderDocument}
            renderOptionsSelect={renderOptionsSelect}
          />
        </div>
      </section>
    );
  }

  return (
    <section
      className="bg-white rounded-lg px-md py-lg shadow-card"
      data-testid={`${testIdPrefix}-block`}
    >
      {expanded ? (
        // Open: title header only — NO chevron. The block can't be collapsed
        // while open; it closes via the section "Save Details".
        <div className="pb-lg">
          <span className="hidden lg:flex text-body-01 font-sans not-italic font-medium leading-normal text-black">
            {sectionTitle} - {client.name}
          </span>
          <span className="flex lg:hidden text-body-01 font-sans not-italic font-medium leading-normal text-black">
            {sectionTitle}
          </span>
        </div>
      ) : (
        // Collapsed: a clickable header (title + chevron) re-opens the block.
        <button
          type="button"
          className="w-full flex items-center justify-between bg-transparent border-0 cursor-pointer focus:outline-none"
          onClick={onToggleCollapse}
          data-testid={`${testIdPrefix}-toggle`}
          aria-expanded={false}
        >
          <span className="hidden lg:flex text-body-01 font-sans not-italic font-medium leading-normal text-black">
            {sectionTitle} - {client.name}
          </span>
          <span className="flex lg:hidden text-body-01 font-sans not-italic font-medium leading-normal text-black">
            {sectionTitle}
          </span>
          <span aria-hidden className="text-black leading-[0]">
            <AppIcon name="chevron compact down" size={24} />
          </span>
        </button>
      )}
      {expanded && (
        <div className="">
          <FieldGrid
            fields={visibleFields(fields, answers, new Set(['*']))}
            answers={answers}
            setAnswer={setAnswer}
            clients={clients}
            testIdPrefix={testIdPrefix}
            renderDocument={renderDocument}
            renderOptionsSelect={renderOptionsSelect}
          />
        </div>
      )}
    </section>
  );
}

/**
 * Public wrapper: provides the placeholder-suppression and advisor-only-field
 * visibility contexts (when the host opts in) around the section editor, so every
 * field input under it can drop its placeholder / hide advisor-only fields.
 * Defaults (no flags) → placeholders shown, advisor-only fields shown → unchanged
 * behaviour for the Advisor accordion and tests.
 */
const EMPTY_LOCKED_FIELDS: ReadonlySet<string> = new Set();

const WizardSectionForm: React.FC<WizardSectionFormProps> = (props) => (
  <SuppressPlaceholdersContext.Provider value={!!props.suppressPlaceholders}>
    <AdvisorOnlyVisibilityContext.Provider value={props.showAdvisorOnlyFields ?? true}>
      <DerivedLockedFieldsContext.Provider value={props.lockedFields ?? EMPTY_LOCKED_FIELDS}>
        <ProviderLogosContext.Provider value={props.providerLogos ?? EMPTY_PROVIDER_LOGOS}>
          <WizardSectionFormInner {...props} />
        </ProviderLogosContext.Provider>
      </DerivedLockedFieldsContext.Provider>
    </AdvisorOnlyVisibilityContext.Provider>
  </SuppressPlaceholdersContext.Provider>
);

export default WizardSectionForm;
